import { z } from 'zod';

import { postSchema } from './post';
import { pulseSchema } from './profile';
import { publicUserSchema } from './user';

export const tagResultSchema = z.object({
  name: z.string(),
  postCount: z.number(),
});

export const searchResultsSchema = z.object({
  query: z.string(),
  posts: z.array(postSchema),
  tags: z.array(tagResultSchema),
  people: z.array(publicUserSchema),
  totalCount: z.number(),
  usedFuzzyFallback: z.boolean(),
});

export const townPulseSchema = z.object({
  place: z.string(),
  countryCode: z.string().nullable(),
  countryFlag: z.string().nullable(),
  town: z.string().nullable(),
  pulse: pulseSchema,
  percentages: z.object({ good: z.number(), bad: z.number(), ideal: z.number() }),
  postsThisWeek: z.number(),
  topTags: z.array(tagResultSchema),
  topIdeal: z
    .object({
      id: z.string(),
      title: z.string().nullable(),
      body: z.string(),
      upvoteCount: z.number(),
      authorUsername: z.string().nullable(),
    })
    .nullable(),
  summary: z.string(),
});

export const notificationActorSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
});

export const notificationSchema = z.object({
  id: z.string(),
  /**
   * Kept in step with the API's enum by a test, because it is not the web
   * app's to decide — a type it has not heard of fails validation and takes
   * the whole page down with it.
   */
  type: z.enum([
    'UPVOTE',
    'COMMENT',
    'REPLY',
    'IDEAL_LINKED',
    'FOLLOW',
    'MENTION',
    'MOD_ACTION',
    'VERIFICATION_APPROVED',
    'VERIFICATION_REJECTED',
    'BUSINESS_RESPONSE',
    'BUSINESS_RESOLUTION',
    'KEYWORD_MATCH',
    'BUSINESS_INVITE',
  ]),
  actors: z.array(notificationActorSchema),
  count: z.number(),
  entityType: z.string(),
  entityId: z.string(),
  excerpt: z.string().nullable(),
  link: z.string().nullable(),
  summary: z.string(),
  isRead: z.boolean(),
  createdAt: z.string(),
  notificationIds: z.array(z.string()),
});

export const notificationPageSchema = z.object({
  items: z.array(notificationSchema),
  unreadCount: z.number(),
});

export const reportReasonSchema = z.enum([
  'SPAM',
  'HARASSMENT',
  'HATE',
  'MISINFORMATION',
  'PERSONAL_INFO',
  'IRRELEVANT_BUSINESS_RESPONSE',
  // Naming a business in a negative post raises the stakes (§11). "This is
  // false" and "somebody is pretending to be us" are different complaints from
  // spam, and a moderator needs to know which one they are looking at.
  'DEFAMATION',
  'IMPERSONATION',
  'OTHER',
]);

export const reportSchema = z.object({
  id: z.string(),
  targetType: z.enum(['POST', 'COMMENT', 'USER']),
  targetId: z.string(),
  reason: reportReasonSchema,
  details: z.string().nullable(),
  status: z.enum(['OPEN', 'ACTIONED', 'DISMISSED']),
  createdAt: z.string(),
  reporter: z.object({ id: z.string(), username: z.string(), displayName: z.string() }),
  target: z
    .object({
      kind: z.enum(['POST', 'COMMENT', 'USER']),
      title: z.string().nullable(),
      body: z.string(),
      authorId: z.string(),
      authorUsername: z.string().nullable(),
      isAnonymous: z.boolean(),
      status: z.string(),
    })
    .nullable(),
});

export const auditEntrySchema = z.object({
  id: z.string(),
  type: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  reason: z.string().nullable(),
  moderator: z.object({ id: z.string(), username: z.string(), displayName: z.string() }),
  createdAt: z.string(),
});

export const blockedUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  blockedAt: z.string(),
});

export type SearchResults = z.infer<typeof searchResultsSchema>;
export type TownPulse = z.infer<typeof townPulseSchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type ReportReason = z.infer<typeof reportReasonSchema>;
export type Report = z.infer<typeof reportSchema>;
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export type TagResult = z.infer<typeof tagResultSchema>;

export const countryStatsSchema = z.object({
  code: z.string(),
  name: z.string(),
  flagEmoji: z.string(),
  good: z.number(),
  bad: z.number(),
  ideal: z.number(),
  total: z.number(),
  goodRatio: z.number().nullable(),
});

export const mapResponseSchema = z.object({
  window: z.enum(['week', 'month', 'all']),
  countries: z.array(countryStatsSchema),
  totalPosts: z.number(),
});

export const hallOfFameEntrySchema = z.object({
  month: z.string(),
  countryCode: z.string(),
  countryName: z.string(),
  countryFlag: z.string(),
  isCurrentMonth: z.boolean(),
  post: postSchema,
});

export type CountryStats = z.infer<typeof countryStatsSchema>;
export type MapResponse = z.infer<typeof mapResponseSchema>;
export type MapWindow = MapResponse['window'];
export type HallOfFameEntry = z.infer<typeof hallOfFameEntrySchema>;
